// src/pages/AttendanceDashboard.jsx — Unified Attendance & History Dashboard
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { 
  RefreshCw, 
  Users, 
  Clock, 
  UserCheck, 
  UserX, 
  Coffee, 
  Search, 
  X, 
  Download, 
  RotateCcw, 
  AlertCircle,
  Activity
} from 'lucide-react';
import './AttendanceDashboard.css';
import { useAuth } from '../context/AuthContext';

export default function AttendanceDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('live'); // 'live' | 'history'

  // Live Status State
  const [employees, setEmployees] = useState([]);
  const [loadingLive, setLoadingLive] = useState(true);

  // History State
  const [allEmployees, setAllEmployees] = useState([]);
  const [historyEmpId, setHistoryEmpId] = useState('');
  const [startDate, setStartDate]       = useState('');
  const [endDate, setEndDate]           = useState('');
  const [datePreset, setDatePreset]     = useState('all');
  const [historySearch, setHistorySearch] = useState('');
  const [records, setRecords]           = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load Live Attendance Data
  const handleResetBreak = async (empId) => {
    if (!window.confirm("Are you sure you want to reset this employee's break hours to 0?")) return;
    try {
      await api.post(`/attendance/reset-break/${empId}`);
      toast.success("Break time reset!");
      loadLive();
    } catch (err) { 
      toast.error("Failed to reset break"); 
    }
  };

  const loadLive = useCallback(async () => {
    setLoadingLive(true);
    try {
      const { data } = await api.get('/attendance');
      setEmployees(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load live attendance');
    } finally {
      setLoadingLive(false);
    }
  }, []);

  // Load Staff List for History Filter
  useEffect(() => {
    api.get('/employees')
      .then(({ data }) => setAllEmployees(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Load History Logs
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const params = {};
      if (historyEmpId) params.employeeId = historyEmpId;
      if (startDate)    params.startDate  = startDate;
      if (endDate)      params.endDate    = endDate;

      const { data } = await api.get('/attendance/history', { params });
      const recordList = Array.isArray(data) ? data : (data.records || []);
      setRecords(recordList);
      setTotalRecords(data.total || recordList.length);
    } catch {
      toast.error('Failed to load attendance logs');
    } finally {
      setLoadingHistory(false);
    }
  }, [historyEmpId, startDate, endDate]);

  useEffect(() => { 
    loadLive(); 
  }, [loadLive]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  // Date range presets handler
  const handleApplyPreset = (preset) => {
    setDatePreset(preset);
    const today = new Date().toISOString().slice(0, 10);
    if (preset === 'today') {
      setStartDate(today);
      setEndDate(today);
    } else if (preset === 'yesterday') {
      const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      setStartDate(y);
      setEndDate(y);
    } else if (preset === 'week') {
      const w = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
      setStartDate(w);
      setEndDate(today);
    } else if (preset === 'month') {
      const m = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
      setStartDate(m);
      setEndDate(today);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  // Live Counts
  const onlineCount  = employees.filter(e => e.onlineStatus === 'online').length;
  const breakCount   = employees.filter(e => e.onlineStatus === 'break').length;
  const offlineCount = employees.filter(e => e.onlineStatus !== 'online' && e.onlineStatus !== 'break').length;
  const activeRate   = Math.round(((onlineCount + breakCount) / (employees.length || 1)) * 100);

  // Filtered History Records (Search)
  const visibleRecords = useMemo(() => {
    if (!historySearch.trim()) return records;
    const q = historySearch.toLowerCase().trim();
    return records.filter(r => 
      (r.employeeName || '').toLowerCase().includes(q) ||
      (r.employeeRole || '').toLowerCase().includes(q) ||
      (r.date || '').toLowerCase().includes(q) ||
      (r.logoutReason || '').toLowerCase().includes(q)
    );
  }, [records, historySearch]);

  // Export CSV
  const handleExportCSV = () => {
    if (!visibleRecords.length) {
      return toast.error("No records to export");
    }
    const headers = ["Date", "Employee", "Role", "Check In", "Check Out", "Duration (Mins)", "Break (Mins)", "Logout Reason"];
    const rows = visibleRecords.map(r => [
      r.date || "",
      r.employeeName || "",
      r.employeeRole || "",
      r.checkInAt ? new Date(r.checkInAt).toLocaleTimeString() : "",
      r.checkOutAt ? new Date(r.checkOutAt).toLocaleTimeString() : (r.checkInAt ? "Active Shift" : ""),
      r.workingMins != null ? r.workingMins : "",
      r.breakMins != null ? r.breakMins : 0,
      r.logoutReason || ""
    ]);

    const csvContent = [headers.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `attendance_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Attendance logs exported!");
  };

  // Helper to get initials
  const getInitials = (name) => {
    if (!name) return "ST";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  // Helper to format minutes
  const formatMins = (mins) => {
    if (mins == null || mins === 0) return "0 mins";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h} hrs`;
    return `${m} mins`;
  };

  return (
    <div className="att-page">
      {/* ── Top Header Bar ── */}
      <div className="att-header-bar">
        <div className="att-header-left">
          <h1 className="att-header-title">Staff Attendance</h1>
          <span className="att-pill-badge">
            <span className="att-pulse-dot" />
            {employees.length} Team Staff
          </span>
          <span className="text-muted small d-none d-md-inline">
            Real-time biometric duty attendance & verified session logs
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button 
            type="button" 
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              fontSize: '12px',
              borderRadius: '8px',
              padding: '6px 12px',
              fontWeight: 600
            }}
            onClick={activeTab === 'live' ? loadLive : loadHistory}
          >
            <RefreshCw size={13} className={loadingLive || loadingHistory ? "spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* ── KPI Stat Cards Grid ── */}
      <div className="att-stat-grid">
        <div className="att-stat-card">
          <div className="att-stat-card__top">
            <span className="att-stat-label">
              {user?.role === 'Agent' ? 'MY WORKSPACE' : user?.role === 'Manager' ? 'TEAM STAFF' : 'TOTAL STAFF'}
            </span>
            <div className="att-stat-card__icon-wrap primary">
              <Users size={18} />
            </div>
          </div>
          <div className="d-flex align-items-baseline justify-content-between">
            <div className="att-stat-value neutral">{employees.length}</div>
            <span className="att-stat-card__rate active">{activeRate}% Active</span>
          </div>
        </div>

        <div className="att-stat-card">
          <div className="att-stat-card__top">
            <span className="att-stat-label">Online Now</span>
            <div className="att-stat-card__icon-wrap success">
              <UserCheck size={18} />
            </div>
          </div>
          <div className="d-flex align-items-baseline justify-content-between">
            <div className="att-stat-value online">{onlineCount}</div>
            <span className="small text-muted">Active duty</span>
          </div>
        </div>

        <div className="att-stat-card">
          <div className="att-stat-card__top">
            <span className="att-stat-label">On Break</span>
            <div className="att-stat-card__icon-wrap warning">
              <Coffee size={18} />
            </div>
          </div>
          <div className="d-flex align-items-baseline justify-content-between">
            <div className="att-stat-value break">{breakCount}</div>
            <span className="small text-muted">Rest session</span>
          </div>
        </div>

        <div className="att-stat-card">
          <div className="att-stat-card__top">
            <span className="att-stat-label">Offline</span>
            <div className="att-stat-card__icon-wrap muted">
              <UserX size={18} />
            </div>
          </div>
          <div className="d-flex align-items-baseline justify-content-between">
            <div className="att-stat-value offline">{offlineCount}</div>
            <span className="small text-muted">Inactive</span>
          </div>
        </div>
      </div>

      {/* ── Main Tabbed Panel ── */}
      <div className="att-main-card">
        {/* Navigation Tabs */}
        <div className="att-tabs-nav">
          <button
            type="button"
            className={`att-tab-btn ${activeTab === 'live' ? 'active' : ''}`}
            onClick={() => setActiveTab('live')}
          >
            <Activity size={15} /> Live Team Status
          </button>
          <button
            type="button"
            className={`att-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <Clock size={15} /> Attendance History & Logs {records.length > 0 && <span className="att-pill-badge ms-1" style={{ fontSize: '11px', padding: '1px 7px' }}>{totalRecords}</span>}
          </button>
        </div>

        {/* ── TAB 1: Live Status ── */}
        {activeTab === 'live' && (
          <div className="att-table-wrap">
            {loadingLive ? (
              <div className="p-4 text-center">
                <div className="crm-skeleton-pulse mb-2" style={{ height: 35, width: '100%' }} />
                <div className="crm-skeleton-pulse mb-2" style={{ height: 35, width: '100%' }} />
                <div className="crm-skeleton-pulse mb-2" style={{ height: 35, width: '100%' }} />
              </div>
            ) : employees.length === 0 ? (
              <div className="att-empty-state">
                <Users size={36} className="att-empty-icon" />
                <div className="att-empty-title">No employees found</div>
                <div className="small text-muted">No staff members are registered under your management hierarchy.</div>
              </div>
            ) : (
              <table className="att-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Current Status</th>
                    <th>Shift Check-In</th>
                    <th>Last Heartbeat</th>
                    <th>Break Used</th>
                    {user?.role !== 'Agent' && <th style={{ width: 90 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {employees.map(emp => {
                    const initials = getInitials(emp.name);
                    const isOnline = emp.onlineStatus === 'online';
                    const isBreak  = emp.onlineStatus === 'break';
                    const breakPct = Math.min(100, Math.round(((emp.breakMins || 0) / 60) * 100));

                    return (
                      <tr key={emp.id || emp._id}>
                        {/* Employee Avatar + Name */}
                        <td>
                          <div className="att-emp-cell">
                            <div className="att-avatar">
                              {initials}
                            </div>
                            <div>
                              <div className="att-emp-name">{emp.name}</div>
                              <div className="att-emp-sub">{emp.email || '—'}</div>
                            </div>
                          </div>
                        </td>

                        {/* Role */}
                        <td>
                          <span className={`att-role-badge ${emp.role || 'Agent'}`}>
                            {emp.role || 'Agent'}
                          </span>
                        </td>

                        {/* Status */}
                        <td>
                          {isOnline ? (
                            <span className="att-status-badge online">
                              <span className="att-pulse-dot" /> Online
                            </span>
                          ) : isBreak ? (
                            <span className="att-status-badge break">
                              <Coffee size={11} /> On Break
                            </span>
                          ) : (
                            <span className="att-status-badge offline">
                              Offline
                            </span>
                          )}
                        </td>

                        {/* Check-In */}
                        <td>
                          <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: '12.5px' }}>
                            <Clock size={12} className="text-primary" />
                            <span>{emp.checkInAt ? new Date(emp.checkInAt).toLocaleTimeString() : 'Not Checked In'}</span>
                          </div>
                        </td>

                        {/* Last Active */}
                        <td>
                          <span className="text-muted" style={{ fontSize: '12px' }}>
                            {emp.lastSeenAt ? new Date(emp.lastSeenAt).toLocaleTimeString() : '—'}
                          </span>
                        </td>

                        {/* Break Time */}
                        <td>
                          <div>
                            <span 
                              className={emp.breakMins >= 60 ? 'text-danger fw-bold' : emp.breakMins > 0 ? 'text-warning fw-semibold' : 'text-muted'}
                              style={{ fontSize: '12px' }}
                            >
                              {emp.breakMins || 0} / 60 mins
                            </span>
                            <div className="att-break-bar-wrap">
                              <div 
                                className="att-break-bar-fill" 
                                style={{ 
                                  width: `${breakPct}%`, 
                                  background: emp.breakMins >= 60 ? 'var(--danger)' : 'var(--warning)' 
                                }} 
                              />
                            </div>
                          </div>
                        </td>

                        {/* Actions */}
                        {user?.role !== 'Agent' && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger py-1 px-2"
                              style={{ fontSize: '11px', borderRadius: '6px', fontWeight: 600 }}
                              onClick={() => handleResetBreak(emp.id)}
                              title="Reset employee break time to 0"
                            >
                              Reset
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── TAB 2: Attendance History ── */}
        {activeTab === 'history' && (
          <div>
            {/* Filter Toolbar */}
            <div className="att-filter-bar">
              <div className="att-filter-bar__left">
                {/* Employee Filter */}
                {user?.role !== 'Agent' && (
                  <select
                    className="att-select-input shadow-sm"
                    value={historyEmpId}
                    onChange={(e) => setHistoryEmpId(e.target.value)}
                  >
                    <option value="">All Staff & Team Records</option>
                    {allEmployees
                      .filter(e => user?.role === 'Admin' || e.reportingTo === user?.id || e.id === user?.id)
                      .map(e => (
                        <option key={e.id || e._id} value={e.id || e._id}>
                          {e.name} ({e.role})
                        </option>
                    ))}
                  </select>
                )}

                {/* Quick Date Presets */}
                <div className="att-preset-pills">
                  <button
                    type="button"
                    className={`att-preset-btn ${datePreset === 'all' ? 'active' : ''}`}
                    onClick={() => handleApplyPreset('all')}
                  >
                    All Time
                  </button>
                  <button
                    type="button"
                    className={`att-preset-btn ${datePreset === 'today' ? 'active' : ''}`}
                    onClick={() => handleApplyPreset('today')}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    className={`att-preset-btn ${datePreset === 'yesterday' ? 'active' : ''}`}
                    onClick={() => handleApplyPreset('yesterday')}
                  >
                    Yesterday
                  </button>
                  <button
                    type="button"
                    className={`att-preset-btn ${datePreset === 'week' ? 'active' : ''}`}
                    onClick={() => handleApplyPreset('week')}
                  >
                    Last 7D
                  </button>
                  <button
                    type="button"
                    className={`att-preset-btn ${datePreset === 'month' ? 'active' : ''}`}
                    onClick={() => handleApplyPreset('month')}
                  >
                    This Month
                  </button>
                </div>

                {/* Start & End Dates */}
                <div className="d-flex align-items-center gap-1">
                  <input
                    type="date"
                    className="att-date-input"
                    value={startDate}
                    onChange={(e) => {
                      setDatePreset('');
                      setStartDate(e.target.value);
                    }}
                    title="Filter Start Date"
                  />
                  <span className="text-muted small">—</span>
                  <input
                    type="date"
                    className="att-date-input"
                    value={endDate}
                    onChange={(e) => {
                      setDatePreset('');
                      setEndDate(e.target.value);
                    }}
                    title="Filter End Date"
                  />
                </div>
              </div>

              <div className="att-filter-bar__right">
                {/* Search in History */}
                <div className="att-search-wrap">
                  <Search size={13} className="att-search-icon" />
                  <input
                    type="text"
                    className="att-search-input"
                    placeholder="Search name, date, role..."
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                  />
                  {historySearch && (
                    <button
                      type="button"
                      className="att-search-clear"
                      onClick={() => setHistorySearch('')}
                      title="Clear search"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* Export CSV */}
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-1"
                  style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontSize: '12px',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontWeight: 600
                  }}
                  onClick={handleExportCSV}
                  title="Export records to CSV"
                >
                  <Download size={13} /> Export
                </button>

                {/* Reset */}
                {(startDate || endDate || historyEmpId || historySearch) && (
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-1 text-muted"
                    style={{
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      fontSize: '12px',
                      borderRadius: '8px',
                      padding: '6px 10px'
                    }}
                    onClick={() => {
                      setHistoryEmpId('');
                      setStartDate('');
                      setEndDate('');
                      setDatePreset('all');
                      setHistorySearch('');
                    }}
                    title="Reset all filters"
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>
            </div>

            {/* History Table */}
            <div className="att-table-wrap">
              {loadingHistory ? (
                <div className="p-4 text-center">
                  <div className="crm-skeleton-pulse mb-2" style={{ height: 35, width: '100%' }} />
                  <div className="crm-skeleton-pulse mb-2" style={{ height: 35, width: '100%' }} />
                  <div className="crm-skeleton-pulse mb-2" style={{ height: 35, width: '100%' }} />
                </div>
              ) : visibleRecords.length === 0 ? (
                <div className="att-empty-state">
                  <AlertCircle size={36} className="att-empty-icon" />
                  <div className="att-empty-title">No attendance records found</div>
                  <div className="small text-muted mt-1">
                    No verified check-in or duty sessions match your selected date range and employee filter.
                  </div>
                </div>
              ) : (
                <table className="att-table">
                  <thead>
                    <tr>
                      <th style={{ width: 140 }}>Date</th>
                      <th>Employee</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Working Hours</th>
                      <th>Break Time</th>
                      <th>Status / Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRecords.map((r, idx) => {
                      const initials = getInitials(r.employeeName || 'Staff');
                      const checkInFormatted = r.checkInAt ? new Date(r.checkInAt).toLocaleTimeString() : '—';
                      const isOngoing = !r.checkOutAt && r.checkInAt;
                      const checkOutFormatted = r.checkOutAt ? new Date(r.checkOutAt).toLocaleTimeString() : (isOngoing ? 'Active Shift' : '—');
                      const durationStr = formatMins(r.workingMins);

                      return (
                        <tr key={r.id || r._id || idx}>
                          {/* Date */}
                          <td style={{ fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                            {r.date || (r.checkInAt ? String(r.checkInAt).slice(0, 10) : '—')}
                          </td>

                          {/* Employee */}
                          <td>
                            <div className="att-emp-cell">
                              <div className="att-avatar" style={{ width: 28, height: 28, fontSize: '11px' }}>
                                {initials}
                              </div>
                              <div>
                                <span className="att-emp-name" style={{ fontSize: '13px' }}>
                                  {r.employeeName || 'Staff'}
                                </span>
                                {r.employeeRole && (
                                  <span className={`att-role-badge ${r.employeeRole} ms-2`} style={{ fontSize: '10px', padding: '1px 5px' }}>
                                    {r.employeeRole}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Check In */}
                          <td>
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12.5px', color: 'var(--text)' }}>
                              <Clock size={12} className="text-success" />
                              <span>{checkInFormatted}</span>
                            </div>
                          </td>

                          {/* Check Out */}
                          <td>
                            {isOngoing ? (
                              <span className="att-status-badge active-shift">
                                <span className="att-pulse-dot" /> Ongoing Shift
                              </span>
                            ) : (
                              <div className="d-flex align-items-center gap-1" style={{ fontSize: '12.5px', color: 'var(--muted)' }}>
                                <Clock size={12} />
                                <span>{checkOutFormatted}</span>
                              </div>
                            )}
                          </td>

                          {/* Working Hours */}
                          <td>
                            <span 
                              className={r.workingMins >= 480 ? 'text-success fw-bold' : r.workingMins >= 240 ? 'text-primary fw-semibold' : 'text-muted'}
                              style={{ fontSize: '13px' }}
                            >
                              {durationStr}
                            </span>
                          </td>

                          {/* Break Time */}
                          <td style={{ fontSize: '12.5px', color: 'var(--muted)' }}>
                            {r.breakMins > 0 ? `${r.breakMins} mins` : '—'}
                          </td>

                          {/* Logout Reason */}
                          <td>
                            {isOngoing ? (
                              <span className="att-status-badge online" style={{ fontSize: '10.5px' }}>
                                Active
                              </span>
                            ) : r.logoutReason === 'manual' ? (
                              <span className="att-status-badge offline" style={{ fontSize: '10.5px' }}>
                                Manual Logout
                              </span>
                            ) : r.logoutReason === 'auto' ? (
                              <span className="att-status-badge offline" style={{ fontSize: '10.5px' }}>
                                Auto Closed
                              </span>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
